import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import monetaryMovementService from '../services/monetaryMovementService';
import { useNotification } from '../context/NotificationContext';
import { formatDate } from '../utils/datetime';
import ConfirmActionModal from './ConfirmActionModal';
import SortableHeader from './SortableHeader';
import '../styles/general.css';

const PER_PAGE = 50;

// Duplicated per page in this codebase rather than shared; following the
// convention rather than starting a fifth pattern.
const formatCurrency = (value) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' })
    .format(parseFloat(value) || 0);

/**
 * The amount as the ledger reads it, not as it is stored.
 *
 * The sign lives in the type, never in the column: `amount` is always
 * positive and `movement_type` says which way the money went. That is a
 * backend invariant, so the minus is added here at the last moment rather
 * than negating anything.
 *
 * An adjustment gets no sign. The model does not record whether it adds or
 * subtracts, so one would be invented.
 */
const displayAmount = (row) => {
  const formatted = formatCurrency(row.amount);
  return row.movement_type === 'outflow' ? `-${formatted}` : formatted;
};

const amountClass = (row) => {
  if (row.movement_type === 'outflow') return 'amount-outflow';
  if (row.movement_type === 'inflow') return 'amount-inflow';
  return '';
};

/**
 * A deactivated row is void first and an outflow second.
 *
 * Red already means "inactive" in these tables, so tinting a deactivated
 * outflow would say the wrong thing twice. The tint marks money that moved;
 * a deactivated entry moved none, and the void class drops the colour.
 */
const rowClass = (row) => {
  if (!row.is_active) return 'table-row-void';
  return row.movement_type === 'outflow' ? 'table-row-outflow' : '';
};

/**
 * One account's ledger.
 *
 * Values, not form controls: the request listed the columns as a select, a
 * date picker and a numeric input, but those were the form's fields copied
 * across. A table lists what happened.
 *
 * There is no Editar action, for anybody. The API exposes no update — a
 * mistake is corrected with an adjustment, which leaves both the error and
 * the correction visible.
 *
 * `reloadToken` is how the parent forces a refetch after the modal saves,
 * without lifting this component's filters and paging into the page.
 */
const MonetaryMovementsTable = ({ account, canWrite, reloadToken }) => {
  const { t } = useTranslation();
  const { notify } = useNotification();

  const [movements, setMovements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [ordering, setOrdering] = useState('-effective_date');
  const [typeFilter, setTypeFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [togglingMovement, setTogglingMovement] = useState(null);
  const [toggling, setToggling] = useState(false);

  const totalPages = Math.ceil(totalCount / PER_PAGE);

  const loadMovements = useCallback(async () => {
    try {
      setLoading(true);
      const params = { account: account.id, ordering, page };
      if (typeFilter) params.movement_type = typeFilter;
      if (dateFrom) params.effective_date_start = dateFrom;
      if (dateTo) params.effective_date_end = dateTo;

      const response = await monetaryMovementService.listMovements(params);
      setMovements(response.data.results || []);
      setTotalCount(response.data.count || 0);
    } catch (err) {
      console.error('Error loading monetary movements:', err);
      notify.error(t('accounting.failedToLoadMovements'));
    } finally {
      setLoading(false);
    }
    // reloadToken is in the list on purpose and the rule is silenced for it:
    // it is never read in the body, it exists so the parent can invalidate
    // this callback and force a refetch after the modal saves. The
    // alternative — remounting via a key — would throw away the user's
    // filters and page every time a movement is added.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account.id, ordering, page, typeFilter, dateFrom, dateTo, reloadToken, notify, t]);

  useEffect(() => {
    loadMovements();
  }, [loadMovements]);

  const handleSortChange = (field) => {
    setOrdering((current) => (current === field ? `-${field}` : field));
    setPage(1);
  };

  const changeFilter = (setter) => (value) => {
    setter(value);
    setPage(1);
  };

  const handleToggleConfirm = async () => {
    if (!togglingMovement) return;
    setToggling(true);
    try {
      await monetaryMovementService.toggleActive(togglingMovement.id);
      notify.success(
        togglingMovement.is_active
          ? t('accounting.movementDeactivated')
          : t('accounting.movementActivated')
      );
      setTogglingMovement(null);
      loadMovements();
    } catch (err) {
      console.error('Error toggling monetary movement:', err);
      notify.error(t('accounting.failedToToggleMovement'));
    } finally {
      setToggling(false);
    }
  };

  if (loading && movements.length === 0) {
    return <div className="loading">{t('common.loading')}</div>;
  }

  return (
    <>
      <div className="filter-panel">
        <div className="filter-row">
          <div className="filter-group">
            <label className="form-label" htmlFor="movementDateFrom">
              {t('accounting.movementFilters.dateFrom')}
            </label>
            <input
              id="movementDateFrom"
              type="date"
              value={dateFrom}
              onChange={(e) => changeFilter(setDateFrom)(e.target.value)}
              className="form-input"
            />
          </div>

          <div className="filter-group">
            <label className="form-label" htmlFor="movementDateTo">
              {t('accounting.movementFilters.dateTo')}
            </label>
            <input
              id="movementDateTo"
              type="date"
              value={dateTo}
              onChange={(e) => changeFilter(setDateTo)(e.target.value)}
              className="form-input"
            />
          </div>

          <div className="filter-group">
            <label className="form-label" htmlFor="movementTypeFilter">
              {t('accounting.movementFilters.type')}
            </label>
            <select
              id="movementTypeFilter"
              value={typeFilter}
              onChange={(e) => changeFilter(setTypeFilter)(e.target.value)}
              className="form-select"
            >
              <option value="">{t('filters.none')}</option>
              <option value="inflow">{t('accounting.movementTypes.inflow')}</option>
              <option value="outflow">{t('accounting.movementTypes.outflow')}</option>
              <option value="adjustment">{t('accounting.movementTypes.adjustment')}</option>
            </select>
          </div>
        </div>
      </div>

      <div className="table-wrapper">
        <table className="table">
          <thead>
            <tr>
              <SortableHeader
                field="movement_type"
                label={t('accounting.movementType')}
                sortBy={ordering}
                onSort={handleSortChange}
              />
              <SortableHeader
                field="effective_date"
                label={t('accounting.effectiveDate')}
                sortBy={ordering}
                onSort={handleSortChange}
              />
              <th>{t('accounting.description')}</th>
              <th className="text-right">{t('accounting.amount')}</th>
              <th className="text-center">{t('common.status')}</th>
              <th className="text-center">{t('common.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {movements.length === 0 ? (
              <tr>
                <td colSpan="6" className="empty-state">
                  {t('accounting.noMovementsYet')}
                </td>
              </tr>
            ) : (
              movements.map((row) => (
                <tr key={row.id} className={rowClass(row)}>
                  <td>{t(`accounting.movementTypes.${row.movement_type}`)}</td>
                  {/* formatDate, not formatInstantDate: effective_date is a
                      calendar date and carries no timezone. */}
                  <td>{formatDate(row.effective_date)}</td>
                  <td>{row.description}</td>
                  <td className="text-right">
                    <span className={amountClass(row)}>{displayAmount(row)}</span>
                  </td>
                  <td className="text-center">
                    <span className={`table-status ${row.is_active ? 'active' : 'inactive'}`}>
                      {row.is_active ? t('common.active') : t('common.inactive')}
                    </span>
                  </td>
                  <td className="text-center table-actions">
                    {canWrite && (
                      <button
                        onClick={() => setTogglingMovement(row)}
                        className={`btn btn-small ${row.is_active ? 'btn-danger' : 'btn-primary'}`}
                      >
                        {row.is_active
                          ? t('accounting.deactivate') : t('accounting.activate')}
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="pagination">
          <button
            onClick={() => setPage(Math.max(1, page - 1))}
            disabled={page === 1}
            className="btn btn-primary"
          >
            {t('common.previous')}
          </button>
          <span className="pagination-info">
            {t('common.pageOf', { page, total: totalPages })}
          </span>
          <button
            onClick={() => setPage(Math.min(totalPages, page + 1))}
            disabled={page === totalPages}
            className="btn btn-primary"
          >
            {t('common.next')}
          </button>
        </div>
      )}

      <ConfirmActionModal
        isOpen={!!togglingMovement}
        onClose={() => setTogglingMovement(null)}
        onConfirm={handleToggleConfirm}
        title={togglingMovement?.is_active
          ? t('accounting.deactivate') : t('accounting.activate')}
        message={togglingMovement?.is_active
          ? t('accounting.confirmDeactivateMovement')
          : t('accounting.confirmActivateMovement')}
        confirmLabel={t('common.confirm')}
        confirming={toggling}
      />
    </>
  );
};

export default MonetaryMovementsTable;
